import { useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { colors } from '@tempmee/tailwind';

import { remoteConfigReady } from '~/api/firebaseConfig';
import { SaltclickChatbot } from '~/constants/ff.posthog';
import { useFeatureFlag } from '~/hooks/useFeatureFlag';
import { useUserSelector } from '~/hooks/useUserSelector';
import { fireHelpChatbotButtonTapped } from '~/posthogs/events';
import { routes } from '~/routes';
import { RemoteConfigUtils } from '~/utils/RemoteConfigUtils';

interface IEmbeddedServiceBootstrap {
  settings: { language?: string };
  init: (orgId: string, deployment: string, siteUrl: string, options: { scrt2URL: string }) => void;
  prechatAPI?: { setHiddenPrechatFields: (fields: Record<string, string>) => void };
}

declare global {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  interface Window {
    embeddedservice_bootstrap?: IEmbeddedServiceBootstrap;
  }
}

const SCRIPT_ID = 'saltclick-bootstrap';
// Salesforce can fail silently (blocked script, CORS on its own subrequests),
// so the skeleton gets a deadline instead of pulsing forever.
const SKELETON_TIMEOUT_MS = 20000;
const STYLE_ID = 'saltclick-visibility';
const VISIBLE_CLASS = 'saltclick-visible';
const READY_CLASS = 'saltclick-ready';

/**
 * Salesforce Embedded Messaging renders everything (button + chat) into a
 * `#embedded-messaging` container and can only be init'd once per page load,
 * so visibility is driven by a body class instead of unloading the widget.
 * The widget renders async after init; the CSS rule keeps it hidden on any
 * route where the Help page isn't mounted, even if init finishes after the
 * user has navigated away.
 *
 * The `::after` skeleton stands in for the launcher until the widget paints —
 * only on Help (`VISIBLE_CLASS`), and only before ready. A landing straight on
 * /help still waits on Salesforce, so the corner shows a placeholder rather
 * than nothing. ponytail: a pseudo-element keeps this out of the React tree;
 * promote it to a real component if it ever needs interaction.
 */
const ensureVisibilityStyle = (): void => {
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = `
    body:not(.${VISIBLE_CLASS}) #embedded-messaging { display: none !important; }
    body.${VISIBLE_CLASS}:not(.${READY_CLASS})::after {
      content: '';
      position: fixed;
      bottom: 1.25rem;
      right: 1.25rem;
      width: 3.75rem;
      height: 3.75rem;
      border-radius: 9999px;
      background: ${colors.neutral[200]};
      animation: saltclick-skeleton-pulse 1.5s ease-in-out infinite;
    }
    @keyframes saltclick-skeleton-pulse { 50% { opacity: 0.4; } }
    @media (prefers-reduced-motion: reduce) {
      body.${VISIBLE_CLASS}:not(.${READY_CLASS})::after { animation: none; }
    }
  `;
  document.head.appendChild(style);
};

/**
 * Embeds the Saltclick (Salesforce Embedded Messaging) chatbot. Called once
 * from the authenticated layout so the widget boots in the background right
 * after login and the launcher is already painted (just hidden) by the time
 * the user opens Help — no load-then-pop. Gated by the
 * `GOTU-4448-saltclick-chatbot` PostHog flag. GOTU-5243.
 */
export const useSaltclickChat = (): void => {
  const flagEnabled = useFeatureFlag(SaltclickChatbot);
  const user = useUserSelector();
  const userId: string | undefined = user?.id;
  // Logged-in users only — never show the widget when Help is reached from
  // login/sign-up (GOTU-5243).
  const enabled = flagEnabled && !!user;
  const isHelpRoute = useLocation().pathname === routes.help;

  const [isMessagingReady, setIsMessagingReady] = useState(false);

  useEffect(() => {
    if (!enabled) return undefined;

    ensureVisibilityStyle();
    window.addEventListener('onEmbeddedMessagingConversationOpened', fireHelpChatbotButtonTapped);

    // The class swaps the skeleton for the real launcher (or drops it when the
    // widget never arrives). It lives on the body, not in state, so it survives
    // the hook remounting on a fresh login.
    const hideSkeleton = () => document.body.classList.add(READY_CLASS);
    const onReady = () => {
      hideSkeleton();
      setIsMessagingReady(true);
    };
    window.addEventListener('onEmbeddedMessagingReady', onReady);
    const skeletonTimer = window.setTimeout(hideSkeleton, SKELETON_TIMEOUT_MS);

    let cancelled = false;
    // Remote config is fetched fire-and-forget at startup, so on a first login
    // the values aren't activated yet — wait for them instead of bailing out.
    void remoteConfigReady.then(() => {
      if (cancelled || document.getElementById(SCRIPT_ID)) return;

      // Same SALTCLICK_CHAT_CONFIG remote-config key as mobile; fails closed
      // (no bundled default) when unset or incomplete.
      const { orgId, deploymentName, siteUrl, scrt2Url } =
        RemoteConfigUtils.getSaltclickChatConfig() ?? {};
      if (!orgId || !deploymentName || !siteUrl || !scrt2Url) {
        console.error('[Saltclick] Missing or incomplete SALTCLICK_CHAT_CONFIG remote config');
        hideSkeleton();
        return;
      }

      const script = document.createElement('script');
      script.id = SCRIPT_ID;
      script.src = `${siteUrl}/assets/js/bootstrap.min.js`;
      script.onerror = () => {
        console.error('[Saltclick] Failed to load bootstrap script');
        hideSkeleton();
      };
      script.onload = () => {
        const bootstrap = window.embeddedservice_bootstrap;
        if (!bootstrap) {
          console.error('[Saltclick] embeddedservice_bootstrap not available after script load');
          hideSkeleton();
          return;
        }
        try {
          bootstrap.settings.language = 'en_US';
          bootstrap.init(orgId, deploymentName, siteUrl, { scrt2URL: scrt2Url });
        } catch (err) {
          console.error('Error loading Embedded Messaging: ', err);
          hideSkeleton();
        }
      };
      document.body.appendChild(script);
    });

    return () => {
      cancelled = true;
      window.clearTimeout(skeletonTimer);
      window.removeEventListener(
        'onEmbeddedMessagingConversationOpened',
        fireHelpChatbotButtonTapped,
      );
      window.removeEventListener('onEmbeddedMessagingReady', onReady);
    };
  }, [enabled]);

  // Session control: Salesforce ties the chat to the admin-panel user id.
  // `prechatAPI` only exists once messaging is ready, so it is the real gate —
  // `isMessagingReady` is just the signal to re-run. Re-sends when the user
  // changes (office switch, or a fresh login in the same tab, where the
  // already-init'd widget never fires the ready event again).
  useEffect(() => {
    if (!userId) return;
    // ponytail: hidden prechat fields only tag *new* conversations, so a user
    // swap in the same tab can still land on the previous user's chat.
    // `userVerificationAPI.clearSession()` did not detach it on our
    // unauthenticated deployment; fixing it needs User Verification (a
    // backend-signed JWT via `setIdentityToken`). GOTU-5243.
    window.embeddedservice_bootstrap?.prechatAPI?.setHiddenPrechatFields({
      gtAdminPanelId: userId,
    });
  }, [isMessagingReady, userId]);

  useEffect(() => {
    if (!enabled || !isHelpRoute) return undefined;
    document.body.classList.add(VISIBLE_CLASS);
    return () => document.body.classList.remove(VISIBLE_CLASS);
  }, [enabled, isHelpRoute]);
};
